import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Edit, Save, X, Plus, Trash2, Users, Eye, Maximize2,
  Trophy, Award, Medal, Star, Crown, Flame, Zap, 
  Target, TrendingUp, Heart, ThumbsUp, CheckCircle,
  BadgeCheck, Sparkles, Rocket, Shield, Gem, Diamond
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { useTheme } from '../../context/ThemeContext';

// Icon mapping
const IconMap = {
  Trophy, Award, Medal, Star, Crown, Flame, Zap, 
  Target, TrendingUp, Heart, ThumbsUp, CheckCircle,
  BadgeCheck, Sparkles, Rocket, Shield, Gem, Diamond
};

const renderIcon = (iconName, className = "w-5 h-5") => {
  const IconComponent = IconMap[iconName];
  return IconComponent ? <IconComponent className={className} /> : <Trophy className={className} />;
};

// Size configurations
const BADGE_SIZES = {
  small: { padding: 'px-1.5 py-0.5', text: 'text-[10px]' },
  medium: { padding: 'px-2 py-1', text: 'text-xs' },
  large: { padding: 'px-3 py-1.5', text: 'text-sm' }
};

const ICON_SIZES = {
  small: 'w-3 h-3',
  medium: 'w-4 h-4',
  large: 'w-5 h-5'
};

const FONT_SIZES = {
  xs: 'text-[10px]',
  sm: 'text-xs',
  base: 'text-sm'
};

const POSITIONS = {
  'top-left': 'top-2 left-2',
  'top-right': 'top-2 right-2',
  'bottom-left': 'bottom-2 left-2',
  'bottom-right': 'bottom-2 right-2'
};

// Preview Card Component - exact replica of homepage trainer card
const TrainerCardPreview = ({ badge, theme }) => {
  const badgeSizeClass = BADGE_SIZES[badge.badge_size || 'medium'];
  const iconSizeClass = ICON_SIZES[badge.icon_size || 'small'];
  const fontSizeClass = FONT_SIZES[badge.font_size || 'xs'];
  const positionClass = POSITIONS[badge.position || 'top-right'];

  return (
    <div className="relative w-full max-w-[320px] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden border-2 border-red-300">
      {/* Image area - exactly like homepage */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-200 to-red-100">
        
        {/* BOOST Badge - Fixed position top-left (like homepage) */}
        <div className="absolute top-3 left-3 z-10">
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-md shadow-lg border-2 border-white text-white font-bold text-sm bg-yellow-500">
            <Crown className="w-4 h-4" />
            <span>BOOSTED PREMIUM1</span>
          </div>
        </div>

        {/* ACHIEVEMENT Badge - Dynamic position */}
        <div className={`absolute ${positionClass} z-10`}>
          <div 
            className={`flex items-center gap-1 ${badgeSizeClass.padding} rounded-md shadow-lg border-2 border-white text-white font-bold ${fontSizeClass}`}
            style={{ backgroundColor: badge.color }}
          >
            {badge.show_icon !== false && renderIcon(badge.icon, iconSizeClass)}
            {badge.show_text !== false && <span>{badge.badge_text || badge.name || 'BADGE'}</span>}
          </div>
        </div>

        {/* Position indicator labels (subtle) */}
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute top-1 left-1 text-[8px] px-1 rounded ${badge.position === 'top-left' ? 'bg-green-500 text-white' : 'bg-white/60 text-gray-400'}`}>↖</div>
          <div className={`absolute top-1 right-1 text-[8px] px-1 rounded ${badge.position === 'top-right' || !badge.position ? 'bg-green-500 text-white' : 'bg-white/60 text-gray-400'}`}>↗</div>
          <div className={`absolute bottom-1 left-1 text-[8px] px-1 rounded ${badge.position === 'bottom-left' ? 'bg-green-500 text-white' : 'bg-white/60 text-gray-400'}`}>↙</div>
          <div className={`absolute bottom-1 right-1 text-[8px] px-1 rounded ${badge.position === 'bottom-right' ? 'bg-green-500 text-white' : 'bg-white/60 text-gray-400'}`}>↘</div>
        </div>
        
        {/* Trainer placeholder image */}
        <div className="absolute inset-0 flex items-center justify-center">
          <img 
            src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'}
            alt="Trainer" 
            className="w-24 h-24 object-contain opacity-30"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      </div>
      
      {/* Trainer info section - exactly like homepage */}
      <div className="p-3 bg-gradient-to-br from-white to-red-50/30">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-700 dark:text-gray-300 font-semibold">
            Price: <span className="font-bold text-red-600">$75/hr</span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-semibold">4.9</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">(42)</span>
            <div className="flex items-center gap-1 bg-indigo-50 px-2 py-1 rounded-full ml-2">
              <Eye className="w-3 h-3 text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-700">156</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <span className="font-bold text-base bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Trainer Name
          </span>
          <Crown className="w-4 h-4 text-yellow-500" />
        </div>

        <p className="text-xs text-gray-700 dark:text-gray-300 mb-1 font-medium flex items-center gap-1">
          <span>💪</span> Fitness • <span>😊</span> Yoga
        </p>

        <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <span className="text-red-500">📍</span>
          Ontario - Toronto
        </p>

        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            🌐 Online
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            ♂️ Male
          </span>
        </div>
      </div>
    </div>
  );
};

// RequirementEditor component
const RequirementEditor = ({ requirements, options, onChange, onAdd, onRemove }) => {
  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold dark:text-white">Requirements (ALL must be met)</Label>
      {requirements.map((req, index) => (
        <div key={`req-${index}`} className="flex gap-2 items-end bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
          <div className="flex-1">
            <Label className="text-xs dark:text-gray-300">Type</Label>
            <select
              value={req.type}
              onChange={(e) => onChange(index, 'type', e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {(options.requirement_types || []).map(rt => (
                <option key={rt.key} value={rt.key}>{rt.label}</option>
              ))}
            </select>
          </div>
          <div className="w-24">
            <Label className="text-xs dark:text-gray-300">Target</Label>
            <Input
              type="number"
              value={req.target}
              onChange={(e) => onChange(index, 'target', parseInt(e.target.value) || 0)}
              className="text-sm"
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs dark:text-gray-300">Description</Label>
            <Input
              value={req.description || ''}
              onChange={(e) => onChange(index, 'description', e.target.value)}
              placeholder="e.g., Get 5 five-star reviews"
              className="text-sm"
            />
          </div>
          {requirements.length > 1 && (
            <Button onClick={() => onRemove(index)} variant="ghost" size="sm" className="text-red-500">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      <Button onClick={onAdd} variant="outline" size="sm">
        <Plus className="w-4 h-4 mr-1" /> Add Requirement
      </Button>
    </div>
  );
};

// Display Settings Editor
const DisplaySettingsEditor = ({ settings, onChange }) => {
  return (
    <div className="space-y-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-lg border border-blue-200 dark:border-gray-600">
      <div className="flex items-center gap-2 mb-3">
        <Maximize2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <Label className="text-sm font-semibold text-blue-800 dark:text-blue-300">Badge Display Settings</Label>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-xs text-gray-600 dark:text-gray-400">Position</Label>
          <select
            value={settings.position || 'top-right'}
            onChange={(e) => onChange('position', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="top-left">Top Left</option>
            <option value="top-right">Top Right</option>
            <option value="bottom-left">Bottom Left</option>
            <option value="bottom-right">Bottom Right</option>
          </select>
        </div>
        
        <div>
          <Label className="text-xs text-gray-600 dark:text-gray-400">Badge Size</Label>
          <select
            value={settings.badge_size || 'medium'}
            onChange={(e) => onChange('badge_size', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
        
        <div>
          <Label className="text-xs text-gray-600 dark:text-gray-400">Font Size</Label>
          <select
            value={settings.font_size || 'xs'}
            onChange={(e) => onChange('font_size', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="xs">Extra Small</option>
            <option value="sm">Small</option>
            <option value="base">Normal</option>
          </select>
        </div>
        
        <div>
          <Label className="text-xs text-gray-600 dark:text-gray-400">Icon Size</Label>
          <select
            value={settings.icon_size || 'small'}
            onChange={(e) => onChange('icon_size', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>
      
      <div className="flex gap-6 mt-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.show_icon !== false}
            onChange={(e) => onChange('show_icon', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show Icon</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.show_text !== false}
            onChange={(e) => onChange('show_text', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show Text</span>
        </label>
      </div>
    </div>
  );
};

const AchievementsManager = () => {
  const { theme } = useTheme();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [options, setOptions] = useState({ icons: [], colors: [], requirement_types: [] });
  const [createForm, setCreateForm] = useState({
    key: '',
    name: '',
    description: '',
    icon: 'Trophy',
    color: '#F59E0B',
    badge_text: '',
    requirements: [{ type: 'five_star_reviews', target: 5, description: '' }],
    sort_order: 0,
    is_active: true,
    // Display settings
    badge_size: 'medium',
    font_size: 'xs',
    icon_size: 'small',
    position: 'top-right',
    show_icon: true,
    show_text: true
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchAchievements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/achievements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAchievements(data.achievements || []);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      toast({ title: "Error", description: "Failed to fetch achievements", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const fetchOptions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/achievements/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchAchievements();
    fetchOptions();
  }, [fetchAchievements, fetchOptions]);

  const handleCreate = async () => {
    if (!createForm.key || !createForm.name) {
      toast({ title: "Error", description: "Key and Name are required", variant: "destructive" });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/achievements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(createForm)
      });

      if (response.ok) {
        toast({ title: "Success", description: "Achievement created successfully" });
        setShowCreateModal(false);
        setCreateForm({
          key: '', name: '', description: '', icon: 'Trophy', color: '#F59E0B',
          badge_text: '', requirements: [{ type: 'five_star_reviews', target: 5, description: '' }],
          sort_order: 0, is_active: true,
          badge_size: 'medium', font_size: 'xs', icon_size: 'small', position: 'top-right',
          show_icon: true, show_text: true
        });
        fetchAchievements();
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail || "Failed to create", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create achievement", variant: "destructive" });
    }
  };

  const handleEdit = (achievement) => {
    setEditing(achievement.achievement_id);
    setEditForm({
      name: achievement.name,
      description: achievement.description,
      icon: achievement.icon,
      color: achievement.color,
      badge_text: achievement.badge_text,
      requirements: achievement.requirements || [],
      sort_order: achievement.sort_order,
      is_active: achievement.is_active,
      // Display settings
      badge_size: achievement.badge_size || 'medium',
      font_size: achievement.font_size || 'xs',
      icon_size: achievement.icon_size || 'small',
      position: achievement.position || 'top-right',
      show_icon: achievement.show_icon !== false,
      show_text: achievement.show_text !== false
    });
  };

  const handleSave = async (achievementId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/achievements/${achievementId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast({ title: "Success", description: "Achievement updated successfully" });
        setEditing(null);
        fetchAchievements();
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail || "Failed to update", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update achievement", variant: "destructive" });
    }
  };

  const handleDelete = async (achievementId) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/achievements/${achievementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: "Success", description: "Achievement deleted successfully" });
        fetchAchievements();
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail || "Failed to delete", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete achievement", variant: "destructive" });
    }
  };

  // Helper functions for requirements
  const handleCreateRequirementChange = useCallback((index, field, value) => {
    setCreateForm(prev => {
      const newReqs = [...prev.requirements];
      newReqs[index] = { ...newReqs[index], [field]: value };
      return { ...prev, requirements: newReqs };
    });
  }, []);

  const handleCreateRequirementAdd = useCallback(() => {
    setCreateForm(prev => ({
      ...prev,
      requirements: [...prev.requirements, { type: 'profile_views', target: 50, description: '' }]
    }));
  }, []);

  const handleCreateRequirementRemove = useCallback((index) => {
    setCreateForm(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  }, []);

  const handleEditRequirementChange = useCallback((index, field, value) => {
    setEditForm(prev => {
      const newReqs = [...prev.requirements];
      newReqs[index] = { ...newReqs[index], [field]: value };
      return { ...prev, requirements: newReqs };
    });
  }, []);

  const handleEditRequirementAdd = useCallback(() => {
    setEditForm(prev => ({
      ...prev,
      requirements: [...prev.requirements, { type: 'profile_views', target: 50, description: '' }]
    }));
  }, []);

  const handleEditRequirementRemove = useCallback((index) => {
    setEditForm(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  }, []);

  const handleCreateDisplayChange = useCallback((field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditDisplayChange = useCallback((field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  }, []);

  if (loading) {
    return (
      <Card><CardContent className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Loading achievements...</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Trainer Achievements
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage achievement badges that trainers can earn</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Achievement
        </Button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold dark:text-white">Create New Achievement</h3>
              <Button onClick={() => setShowCreateModal(false)} variant="ghost" size="sm"><X className="w-5 h-5" /></Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Key (unique) *</Label>
                    <Input
                      value={createForm.key}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                      placeholder="e.g., popular_trainer"
                    />
                  </div>
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={createForm.name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Popular Trainer"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Input
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Explain how to earn this achievement"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Icon</Label>
                    <select
                      value={createForm.icon}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {(options.icons || []).map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={createForm.color}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-12 h-10 p-1"
                      />
                      <Input 
                        value={createForm.color} 
                        onChange={(e) => setCreateForm(prev => ({ ...prev, color: e.target.value }))} 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Badge Text</Label>
                    <Input
                      value={createForm.badge_text}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, badge_text: e.target.value }))}
                      placeholder="e.g., POPULAR"
                    />
                  </div>
                </div>

                {/* Display Settings */}
                <DisplaySettingsEditor 
                  settings={createForm} 
                  onChange={handleCreateDisplayChange} 
                />

                <RequirementEditor
                  requirements={createForm.requirements}
                  options={options}
                  onChange={handleCreateRequirementChange}
                  onAdd={handleCreateRequirementAdd}
                  onRemove={handleCreateRequirementRemove}
                />
              </div>

              {/* Right: Preview */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <Label className="text-sm font-semibold">Live Preview (as seen on homepage)</Label>
                </div>
                
                <div className="flex justify-center p-6 bg-gray-100 rounded-xl">
                  <TrainerCardPreview theme={theme} badge={createForm} />
                </div>

                {/* Mini Badge Preview */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <Label className="text-xs text-gray-500 dark:text-gray-400 mb-2 block">Badge Preview</Label>
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: `${createForm.color}20` }}>
                      {renderIcon(createForm.icon, "w-8 h-8")}
                    </div>
                    <div>
                      <Badge style={{ backgroundColor: createForm.color, color: 'white' }}>
                        {createForm.show_icon && renderIcon(createForm.icon, "w-4 h-4 inline mr-1")}
                        {createForm.show_text && (createForm.badge_text || createForm.name || 'BADGE')}
                      </Badge>
                      <p className="text-sm font-semibold mt-1">{createForm.name || 'Achievement Name'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button onClick={() => setShowCreateModal(false)} variant="outline">Cancel</Button>
              <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">Create Achievement</Button>
            </div>
          </div>
        </div>
      )}

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {achievements.map((achievement) => (
          <Card key={achievement.achievement_id} className={`relative ${!achievement.is_active ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl shadow-sm" style={{ backgroundColor: `${achievement.color}15` }}>
                    {renderIcon(achievement.icon, "w-8 h-8")}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{achievement.name}</CardTitle>
                    <code className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{achievement.key}</code>
                  </div>
                </div>
                <div className="flex gap-1">
                  {editing === achievement.achievement_id ? (
                    <>
                      <Button onClick={() => handleSave(achievement.achievement_id)} size="sm" variant="ghost" className="text-green-600"><Save className="w-4 h-4" /></Button>
                      <Button onClick={() => setEditing(null)} size="sm" variant="ghost"><X className="w-4 h-4" /></Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => handleEdit(achievement)} size="sm" variant="ghost"><Edit className="w-4 h-4" /></Button>
                      <Button onClick={() => handleDelete(achievement.achievement_id)} size="sm" variant="ghost" className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              {editing === achievement.achievement_id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input 
                        value={editForm.name} 
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} 
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Badge Text</Label>
                      <Input 
                        value={editForm.badge_text} 
                        onChange={(e) => setEditForm(prev => ({ ...prev, badge_text: e.target.value }))} 
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input 
                      value={editForm.description} 
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} 
                    />
                  </div>
                  
                  {/* Display Settings in Edit Mode */}
                  <DisplaySettingsEditor 
                    settings={editForm} 
                    onChange={handleEditDisplayChange} 
                  />

                  {/* Edit Preview */}
                  <div className="flex justify-center p-4 bg-gray-100 rounded-lg">
                    <TrainerCardPreview theme={theme} badge={editForm} />
                  </div>

                  <RequirementEditor
                    requirements={editForm.requirements}
                    options={options}
                    onChange={handleEditRequirementChange}
                    onAdd={handleEditRequirementAdd}
                    onRemove={handleEditRequirementRemove}
                  />
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{achievement.description}</p>
                  
                  {/* Compact Preview */}
                  <div className="flex justify-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <TrainerCardPreview theme={theme} badge={achievement} />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-gray-500 dark:text-gray-400">Requirements</Label>
                    <div className="mt-1 space-y-1">
                      {(achievement.requirements || []).map((req, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{req.description || `${req.type}: ${req.target}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 pt-3 border-t">
                    <Badge style={{ backgroundColor: achievement.color, color: 'white' }}>
                      {achievement.show_icon !== false && renderIcon(achievement.icon, "w-3 h-3 inline mr-1")}
                      {achievement.show_text !== false && achievement.badge_text}
                    </Badge>
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{achievement.earned_count || 0} trainers earned</span>
                    </div>
                    <Badge variant={achievement.is_active ? "default" : "secondary"} className="ml-auto">
                      {achievement.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {achievements.length === 0 && (
        <Card><CardContent className="p-8 text-center">
          <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">No Achievements Found</h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Create your first achievement badge to get started.</p>
          <Button onClick={() => setShowCreateModal(true)} className="mt-4"><Plus className="w-4 h-4 mr-2" /> Create Achievement</Button>
        </CardContent></Card>
      )}
    </div>
  );
};

export default AchievementsManager;
